import { appConfig } from 'app/app.config';
import {
  BrowserCacheLocation,
  IPublicClientApplication,
  InteractionType,
  PublicClientApplication,
} from '@azure/msal-browser';
import { environment } from './environments/environment';
import { MsalGuardConfiguration, MsalInterceptorConfiguration } from '@azure/msal-angular';
import { bootstrapApplication } from '@angular/platform-browser';
import { AppComponent } from './app/app.component';
import * as Sentry from '@sentry/angular';

import { beforeSendScrubbed } from './app/core/sentry/scrub-event';

const sentryConfig = environment.sentry;

// Whether Sentry runs is a per-environment config decision (`sentry.enabled`), never a build-mode
// one (#20997): the `test` and `demo` build configurations run with `optimization: false`, which
// leaves `ngDevMode` truthy — an `isDevMode()` gate here silently killed Sentry on both, so the
// toolbar feedback button rendered (config says enabled) but had nothing to attach to.
// Local `ng serve` stays quiet because environment.ts sets `sentry.enabled: false`.
// All four brand production environments end in 'production' (production, dotpersoneel-,
// transportplatform-, vakgarage-), so a new brand inherits the stricter setting by default rather
// than by someone remembering to add it.
const isProductionSentryEnvironment = !!sentryConfig?.environment?.endsWith('production');

if (sentryConfig?.enabled && sentryConfig.dsn) {
  const integrations = [];

  const sessionSampleRate = sentryConfig.replaysSessionSampleRate ?? 0;
  const onErrorSampleRate = sentryConfig.replaysOnErrorSampleRate ?? 0;

  if (sessionSampleRate > 0 || onErrorSampleRate > 0) {
    integrations.push(
      Sentry.replayIntegration({
        // Blanket masking on production only.
        //
        // `maskAllText` masks EVERY string in the DOM, not just data: labels, headings, buttons
        // and navigation all become asterisks (the SDK's own default maskFn is `'*'.repeat(n)`).
        // A replay then shows layout and clicks but not what anything said, which is close to
        // useless for diagnosis. So it is off everywhere the data is not real, and on where a leak
        // cannot be undone. The targeted `.sentry-mask` below is what keeps production replays
        // partly readable; blanket masking is the belt on top of those braces.
        maskAllText: isProductionSentryEnvironment,
        // Keep user-entered values in form controls masked.
        maskAllInputs: true,
        // Allow images/logos to render in replays.
        blockAllMedia: false,
        // Opt-in masking/blocking: add the `sentry-mask` or `sentry-block` class
        // to any element that displays sensitive data as plain text.
        mask: ['.sentry-mask'],
        unmask: ['.sentry-unmask'],
        block: ['.sentry-block'],
      })
    );
  }

  if (environment.sentry.feedbackEnabled && typeof Sentry.feedbackIntegration === 'function') {
    integrations.push(
      Sentry.feedbackIntegration({
        colorScheme: 'system',
        // No auto-injected floating button (#20919): it owned the bottom-right corner permanently
        // and collided with whatever else lives there (the widget "+" FAB, the table's bulk-actions
        // bar, the "Aan de slag" panel). The trigger now sits in the app toolbar, and
        // SentryFeedbackService mounts Sentry's own actor only for the surfaces that have no
        // reachable toolbar. Everything else about the widget is unchanged.
        autoInject: false,
        enableScreenshot: true,
        triggerLabel: 'Feedback',
        triggerAriaLabel: 'Feedback openen',
        formTitle: 'Meld een probleem of idee',
        submitButtonLabel: 'Verstuur',
        cancelButtonLabel: 'Annuleren',
        messageLabel: 'Beschrijving',
        messagePlaceholder: 'Beschrijf wat er gebeurde of wat je verwachtte.',
        nameLabel: 'Naam',
        namePlaceholder: 'Jouw naam',
        emailLabel: 'E-mailadres',
        emailPlaceholder: 'naam@bedrijf.nl',
        successMessageText: 'Bedankt voor je feedback!',
        isRequiredLabel: '(verplicht)',
        addScreenshotButtonLabel: 'Screenshot toevoegen',
        removeScreenshotButtonLabel: 'Screenshot verwijderen',
        highlightToolText: 'Markeren',
        hideToolText: 'Verbergen',
        removeHighlightText: 'Markering verwijderen',
        useSentryUser: {
          email: 'email',
          name: 'username',
        },
      })
    );
  }

  Sentry.init({
    dsn: sentryConfig.dsn,
    environment: sentryConfig.environment,
    sendDefaultPii: sentryConfig.sendDefaultPii,
    tracesSampleRate: sentryConfig.tracesSampleRate,
    replaysSessionSampleRate: sessionSampleRate,
    replaysOnErrorSampleRate: onErrorSampleRate,
    integrations,
    // Last line of defence: strip request/response payloads and query strings from every event,
    // whatever produced it. See core/sentry/scrub-event.ts for what and why.
    beforeSend: beforeSendScrubbed,
  });
}

/**
 * MSAL tracks its redirect state in one origin-wide key, `msal.interaction.status`, whose value is
 * the clientId that started the interaction. It only clears that key when the value matches its own
 * clientId (BrowserCacheManager.setInteractionInProgress), and `handleRedirectPromise` bails out
 * early on a foreign value without cleaning up. Switching tenant on a shared origin — every local
 * environment runs on localhost:4200 — therefore leaves an entry no instance is able to remove, and
 * every subsequent login throws `BrowserAuthError: interaction_in_progress` for good.
 *
 * Note the key lives in sessionStorage, not localStorage: we only set `cacheLocation`, so
 * `temporaryCacheLocation` keeps its SessionStorage default. MSAL mirrors the same key into a
 * cookie when `storeAuthStateInCookie` is true and reads that cookie first, so sessionStorage is
 * only the whole story while that option stays off — which it now is, the IE 11 toggle that set it
 * having been dropped along with IE support.
 *
 * Dropping an entry owned by a different clientId is safe — this instance can never complete or
 * clear that interaction. An entry carrying our own clientId is left alone, so a genuine in-flight
 * redirect still behaves as MSAL intends.
 */
function clearForeignMsalInteractionStatus(): void {
  // = TemporaryCacheKeys.INTERACTION_STATUS_KEY, which @azure/msal-browser does not export.
  // A rename on their side makes this read null and the fix a no-op — it never throws.
  const key = 'msal.interaction.status';

  try {
    const owner = sessionStorage.getItem(key);

    if (owner && owner !== environment.msalConfig.auth.clientId) {
      sessionStorage.removeItem(key);
    }
  } catch {
    // Touching sessionStorage throws when the browser denies site data (blocked cookies,
    // sandboxed iframe). MSAL guards the same access and falls back to MemoryStorage
    // (BrowserCacheManager.setupBrowserStorage); an unguarded throw here would escape the
    // DI factory and take down bootstrap. With no persistent store there is no stale key
    // to clear either, so swallowing is the correct behaviour, not a silenced error.
  }
}

export function MSALInstanceFactory(): IPublicClientApplication {
  clearForeignMsalInteractionStatus();

  return new PublicClientApplication({
    auth: {
      clientId: environment.msalConfig.auth.clientId,
      authority: environment.b2cPolicies.authorities.signUpSignIn.authority,
      redirectUri: environment.msalConfig.auth.redirectUri,
      postLogoutRedirectUri: '/',
      knownAuthorities: [environment.b2cPolicies.authorityDomain],
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
    },
    system: {
      allowNativeBroker: false, // Disables WAM Broker
    },
  });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set(environment.apiConfig.uri, environment.apiConfig.scopes);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: [...environment.apiConfig.scopes],
    },
    loginFailedRoute: '/signed-in-redirect',
  };
}

bootstrapApplication(AppComponent, appConfig).catch((err: any) => console.log(err));
